import type { ComponentChildren } from 'preact';
import { clearToken, data, error, fetching, history, refresh, token } from '../app/store.ts';
import { AppCard } from './AppCard.tsx';
import { TokenPrompt } from './TokenPrompt.tsx';

function Banner({ children }: { children: ComponentChildren }) {
  return <div class="banner">{children}</div>;
}

function Banners() {
  const status = data.value;
  if (!status) return null;
  const out: ComponentChildren[] = [];

  if (!status.pointerHost) {
    out.push(
      <Banner key="unset">KV pointer SERVER_CONFIG/current is unset — no app is live.</Banner>,
    );
  } else if (!status.liveApp) {
    out.push(
      <Banner key="unknown">
        KV pointer names unknown host <code>{status.pointerHost}</code> — not one of the blue/green
        pair. Deploys will refuse to flip until this is fixed.
      </Banner>,
    );
  }

  const live = status.apps.find((a) => a.live);
  if (live && live.flyError === null && !live.machines.some((m) => m.state === 'started')) {
    out.push(
      <Banner key="no-machine">
        Live app <code>{live.name}</code> has no running machine — players will cold-start it.
      </Banner>,
    );
  }

  const allDraining =
    status.apps.length > 0 &&
    status.apps.every((a) => !!(a.health?.draining || a.status?.draining || a.metrics?.draining));
  if (allDraining && status.apps.some((a) => a.health || a.status || a.metrics)) {
    out.push(
      <Banner key="drained">Both apps are draining — no server is accepting new lobbies.</Banner>,
    );
  }

  return out.length > 0 ? <div class="banners">{out}</div> : null;
}

export function App() {
  if (!token.value) return <TokenPrompt />;

  const status = data.value;
  return (
    <div class="dash">
      <header class="dash-header">
        <h1>Accreto Admin</h1>
        <div class="dash-actions">
          {error.value && <span class="fetch-error">refresh failed: {error.value}</span>}
          {status && (
            <span class="fetched-at">
              {fetching.value
                ? 'refreshing…'
                : `fetched ${new Date(status.fetchedAt).toLocaleTimeString()}`}
            </span>
          )}
          <button class="dash-btn" onClick={() => void refresh()} disabled={fetching.value}>
            Refresh
          </button>
          <button class="dash-btn" onClick={clearToken}>
            Change token
          </button>
        </div>
      </header>

      <Banners />

      {!status && !error.value && <div class="dash-loading">loading…</div>}
      {!status && error.value && (
        <div class="dash-loading">could not reach /api/status: {error.value}</div>
      )}

      {status && (
        <main class="cards">
          {status.apps.map((app) => (
            <AppCard key={app.name} app={app} samples={history.value[app.name] ?? []} />
          ))}
        </main>
      )}
    </div>
  );
}
